import React from 'react';
import { Link, mergeClasses } from '@fluentui/react-components';
import { ArrowLeftRegular } from '@fluentui/react-icons';
import { usePolicyBreadcrumbStyles } from './PolicyBreadcrumb.styles';

export interface PolicyBreadcrumbItem {
    label: string;
    href: string;
    onClick?: (event: React.MouseEvent<HTMLAnchorElement>) => void;
}

interface PolicyBreadcrumbProps {
    /** Ancestor pages, outermost first. The first one gets the back arrow. */
    items: PolicyBreadcrumbItem[];
    /** Title of the current page, rendered last with `aria-current="page"`. */
    current?: string;
}

/** Breadcrumb trail exposed as a navigation landmark containing an ordered list of items. */
export const PolicyBreadcrumb: React.FC<PolicyBreadcrumbProps> = ({ items, current }) => {
    const styles = usePolicyBreadcrumbStyles();
    const separator = (
        <span className={styles.separator} aria-hidden="true">
            &gt;
        </span>
    );

    return (
        <nav aria-label="Breadcrumb">
            <ol className={styles.list}>
                {items.map((item, index) => (
                    <li key={item.href} className={styles.item}>
                        {/* `inline` underlines the link so it isn't distinguished from the current-page text by color alone. */}
                        <Link
                            inline
                            className={mergeClasses(styles.link, index === 0 && styles.homeLink)}
                            href={item.href}
                            onClick={item.onClick}
                        >
                            {index === 0 && <ArrowLeftRegular fontSize={14} aria-hidden="true" />}
                            {item.label}
                        </Link>
                        {(index < items.length - 1 || current) && separator}
                    </li>
                ))}
                {current && (
                    <li className={styles.item}>
                        <span className={styles.current} aria-current="page">
                            {current}
                        </span>
                    </li>
                )}
            </ol>
        </nav>
    );
};
